import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { setSession, getToken, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Loader2, Store, Phone, Lock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a2dee35-189a-4f59-95bd-a089560b8efb/artifacts/rzc72v1x_thara%20%281%29.png";

// POS login — Store/Franchise Code first, then mobile + password. Enforced
// server-side to match the assigned user location.
export default function POSLogin() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // POS users get their own token space; check current user
  const u = getUser();
  const alreadyLoggedIn = getToken() && u && ["branch", "franchise"].includes(u.department);

  // Public list of stores for the picker.
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pos/stores-public`)
      .then((r) => r.json())
      .then((data) => setStores((data || []).filter((b) => ["branch", "franchise"].includes(b.branch_type))))
      .catch(() => setStores([]));
  }, []);

  if (alreadyLoggedIn) return <Navigate to="/pos" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login-pos", { store_id: storeId, mobile: mobile.trim(), password });
      setSession(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name}`);
      navigate("/pos", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "POS login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex bg-slate-950 text-white" data-testid="pos-login">
      <Toaster richColors position="top-right" />
      <div className="hidden md:flex flex-col justify-center gap-6 md:w-1/2 xl:w-[55%] px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-red-950 to-black" />
        <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-red-500/20 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative z-10 max-w-md space-y-6">
          <div className="bg-white rounded-lg p-5 inline-flex"><img src={LOGO_URL} alt="Thara" className="h-16 w-auto" /></div>
          <div>
            <h1 className="font-heading text-4xl font-bold leading-tight">Thara Gold POS</h1>
            <p className="text-white/70 mt-2">Terminal-based billing for every Branch and Franchise store.</p>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
            <Info k="Barcode Scan" v="Fast" />
            <Info k="Hold / Resume" v="✓" />
            <Info k="Multi-pay" v="Cash · Card · UPI" />
          </div>
        </div>
      </div>
      <div className="flex-1 md:w-1/2 xl:w-[45%] flex items-center justify-center px-8 py-6 bg-white text-gray-900">
        <div className="w-full max-w-md space-y-6">
          <div className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold">POS Terminal Login</p>
            <h2 className="font-heading text-3xl font-bold">Sign in to your store.</h2>
          </div>
          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Store / Franchise *</Label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select required value={storeId} onChange={(e) => setStoreId(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-md border border-gray-200 bg-white" data-testid="pos-store">
                  <option value="">— Pick your store —</option>
                  <optgroup label="Branches">
                    {stores.filter((s) => s.branch_type === "branch").map((s) => <option key={s.id} value={s.id}>🏢 {s.name}</option>)}
                  </optgroup>
                  <optgroup label="Franchises">
                    {stores.filter((s) => s.branch_type === "franchise").map((s) => <option key={s.id} value={s.id}>🏪 {s.name}</option>)}
                  </optgroup>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Mobile *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input required value={mobile} onChange={(e) => setMobile(e.target.value)} className="pl-10 h-11" placeholder="9876543210" data-testid="pos-mobile" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-11" data-testid="pos-password" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="pos-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In to POS"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Info({ k, v }) {
  return <div><p className="text-lg font-bold text-amber-300">{v}</p><p className="text-[10px] uppercase tracking-widest text-white/60">{k}</p></div>;
}
